import { cn } from '@rental/ui'
import { CircleAlert, CircleCheck, Info, type LucideIcon, Minus, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'

export type ChipTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

/**
 * The one map from a status to its look (DESIGN.md, "Colour"): an icon, the
 * word and a colour together, never the colour alone; each 800 ink on its
 * own tint, and information logo blue on the info tint.
 */
const CHIP: Readonly<Record<ChipTone, { icon: LucideIcon; className: string }>> = {
  success: { icon: CircleCheck, className: 'bg-mz-success-tint text-mz-success-800' },
  warning: { icon: TriangleAlert, className: 'bg-mz-warning-tint text-mz-warning-800' },
  danger: { icon: CircleAlert, className: 'bg-mz-danger-tint text-mz-danger-800' },
  info: { icon: Info, className: 'bg-mz-info-tint text-brand' },
  neutral: { icon: Minus, className: 'bg-surface-well text-text-muted' },
}

/** `compact`: in a narrow place, a lock screen's name tile (UI-2a): the small text, less padding. */
export function StatusChip({
  tone,
  compact = false,
  children,
}: {
  tone: ChipTone
  compact?: boolean
  children: ReactNode
}) {
  const { icon: Icon, className } = CHIP[tone]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-control font-medium',
        compact ? 'px-2 text-xs' : 'px-3 py-1 text-sm',
        className,
      )}
    >
      <Icon aria-hidden="true" className={cn('shrink-0', compact ? 'size-3' : 'size-4')} />
      {children}
    </span>
  )
}
